import type { Translations } from '@/i18n/locales/en';
import type { OptionRules, ProductOption } from '@restaurant/api-contracts';
import {
  legacyIssues,
  summarizeSelection,
  type ClampPlan,
  LegacyIssue,
  LimitField,
  LimitInputError,
  LimitSummary,
} from './option-limits';

type Copy = Translations['menu']['optionLimits'];

export const summaryText = (summary: LimitSummary, copy: Copy): string => {
  switch (summary.kind) {
    case 'exactly':
      return copy.summary.exactly(summary.n);
    case 'upTo':
      return copy.summary.upTo(summary.n);
    case 'range':
      return copy.summary.range(summary.min, summary.max);
    case 'atLeast':
      return copy.summary.atLeast(summary.n);
    case 'any':
      return copy.summary.any;
  }
};

export const phraseText = (summary: LimitSummary, copy: Copy): string => {
  switch (summary.kind) {
    case 'exactly':
      return copy.phrase.exactly(summary.n);
    case 'upTo':
      return copy.phrase.upTo(summary.n);
    case 'range':
      return copy.phrase.range(summary.min, summary.max);
    case 'atLeast':
      return copy.phrase.atLeast(summary.n);
    case 'any':
      return copy.phrase.any;
  }
};

export const legacyText = (issue: LegacyIssue, copy: Copy): string => {
  switch (issue.kind) {
    case 'minAboveN':
      return copy.legacy.minAboveN(issue.min, issue.n);
    case 'maxAboveN':
      return copy.legacy.maxAboveN(issue.max, issue.n);
    case 'minAboveMax':
      return copy.legacy.minAboveMax(issue.min, issue.max);
  }
};

export const inputErrorText = (error: LimitInputError, field: LimitField, copy: Copy): string => {
  switch (error.kind) {
    case 'notInteger':
      return copy.notInteger;
    case 'range':
      return copy.outOfRange(field === 'min' ? copy.minimum : copy.maximum, error.lo, error.hi);
    case 'minAboveMax':
      return copy.minAboveMax(error.max);
    case 'maxBelowMin':
      return copy.maxBelowMin(error.min);
  }
};

export const clampBodyText = (
  plan: ClampPlan,
  optionName: string,
  choiceName: string,
  copy: Copy,
): string =>
  `${copy.clampBody(optionName, choiceName, plan.n, summaryText(plan.rule, copy))} ${copy.clampChange[plan.change](plan.n)}`;

export const optionPickLabel = (
  option: { option: { choices: ProductOption['choices'] }; rulesAfter: OptionRules },
  copy: Copy,
): string => {
  const phrase = phraseText(summarizeSelection(option.rulesAfter), copy);
  const broken = legacyIssues({ ...option.rulesAfter, choices: option.option.choices }).length > 0;
  return broken ? `${phrase} - ${copy.fixRule}` : phrase;
};
